import { useEffect, useState, type FormEvent, type ReactNode } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { useForm } from "@tanstack/react-form"
import { Eye, EyeOff } from "lucide-react"

import { Button } from "../components/ui"
import { api, ApiError, authRequest } from "../lib/api"
import { authClient } from "../lib/auth-client"
import { useQueryClient } from "@tanstack/react-query"
import { TurnstileField } from "../components/turnstile"

function Brand() {
  return <div className="text-lg font-semibold tracking-tight">Prospera Office</div>
}

function Alert({ children }: { children: ReactNode }) {
  return <div role="alert" className="bg-destructive text-background mt-4 rounded-lg border border-destructive p-3 text-sm">{children}</div>
}

function Field({ label, name, type, autoComplete, value, onChange }: { label: string; name: string; type: string; autoComplete: string; value?: string; onChange?: (value: string) => void }) {
  return (
    <label className="mt-5 block text-sm font-semibold">
      {label}
      <input required name={name} type={type} autoComplete={autoComplete} value={value} onChange={onChange ? (event) => onChange(event.target.value) : undefined} className="bg-card mt-2 h-11 w-full rounded-lg border border-input px-3.5 text-base outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
    </label>
  )
}

function PasswordField({ label, name, autoComplete, value, onChange }: { label: string; name: string; autoComplete: string; value?: string; onChange?: (value: string) => void }) {
  const [visible, setVisible] = useState(false)
  return <label className="mt-5 block text-sm font-semibold">{label}<span className="relative mt-2 block"><input required name={name} type={visible ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={onChange ? (event) => onChange(event.target.value) : undefined} className="bg-card h-11 w-full rounded-lg border border-input px-3.5 pr-11 text-base outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" /><button type="button" className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 px-3" onClick={() => setVisible((current) => !current)} aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}>{visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</button></span></label>
}

function AuthForm({ title, onSubmit, children }: { title: string; onSubmit: (event: FormEvent<HTMLFormElement>) => void; children: ReactNode }) {
  return <main className="bg-background h-screen overflow-y-auto overscroll-contain"><section className="mx-auto flex min-h-full w-full max-w-md items-center px-5 py-8"><form onSubmit={onSubmit} className="bg-card w-full rounded-lg border border-border p-6 shadow-sm sm:p-8"><Brand /><h1 className="mt-8 text-2xl font-semibold tracking-tight">{title}</h1>{children}</form></section></main>
}

export function StaffSignIn({ onSuccess, onMfaRequired }: { onSuccess: () => void | Promise<void>; onMfaRequired: () => void }) {
  const [error, setError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const form = useForm({
    defaultValues: { email: "", password: "" },
    onSubmit: async ({ value }) => {
      const email = value.email.trim().toLowerCase()
      const password = value.password
      if (!email || !password) {
        setError("Enter your staff email address and password.")
        return
      }
      setError("")
      try {
        const result = await authRequest<{ twoFactorRedirect?: boolean; error?: { message?: string } }>("/sign-in/email", { email, password, turnstileToken })
        if (result.error) setError(result.error.message ?? "Sign-in failed.")
        else if (result.twoFactorRedirect) onMfaRequired()
        else await onSuccess()
      } catch (cause) {
        setError(
          cause instanceof ApiError && cause.status === 401
            ? "The email address or password is incorrect."
            : cause instanceof ApiError
              ? cause.message
              : "Sign-in could not be completed. Try again."
        )
      }
    },
  })
  return <AuthForm title="Staff sign in" onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit() }}><form.Field name="email">{(field) => <label className="mt-5 block text-sm font-semibold">Work email<input required name={field.name} type="email" autoComplete="username" value={field.state.value} onChange={(event) => field.handleChange(event.target.value)} className="bg-card mt-2 h-11 w-full rounded-lg border border-input px-3.5 text-base outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" /></label>}</form.Field><form.Field name="password">{(field) => <PasswordField label="Password" name={field.name} autoComplete="current-password" value={field.state.value} onChange={(value) => field.handleChange(value)} />}</form.Field><TurnstileField onToken={setTurnstileToken} /><Button className="mt-6 w-full" disabled={form.state.isSubmitting}>{form.state.isSubmitting ? "Signing in…" : "Sign in"}</Button><Link to="/forgot-password" className="text-muted-foreground mt-4 block text-center text-sm underline underline-offset-4">Forgot password?</Link>{error && <Alert>{error}</Alert>}</AuthForm>
}

export function StaffMfaChallenge() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [code, setCode] = useState("")
  const [backup, setBackup] = useState(false)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError("")
    try {
      const result = backup ? await authClient.twoFactor.verifyBackupCode({ code: code.trim() }) : await authClient.twoFactor.verifyTotp({ code: code.trim(), trustDevice: true })
      if (result.error) setError(result.error.message ?? "The verification code is invalid.")
      else { await queryClient.invalidateQueries({ queryKey: ["admin-me"] }); await navigate({ to: "/dashboard", replace: true }) }
    } catch {
      setError("Verification could not be completed. Try again.")
    } finally {
      setBusy(false)
    }
  }
  return <AuthForm title="Verification code" onSubmit={verify}><p className="text-muted-foreground mt-2 text-sm">{backup ? "Enter one of your recovery codes." : "Enter the code from your authenticator app."}</p><Field label={backup ? "Recovery code" : "Authentication code"} name="code" type="text" autoComplete="one-time-code" value={code} onChange={(value) => setCode(value)} /><Button className="mt-6 w-full" disabled={busy || code.trim().length < 6}>{busy ? "Verifying…" : "Verify"}</Button><button type="button" className="text-muted-foreground mt-4 w-full text-sm underline" onClick={() => { setBackup((current) => !current); setCode(""); setError("") }}>{backup ? "Use authenticator code" : "Use a recovery code"}</button>{error && <Alert>{error}</Alert>}</AuthForm>
}

export function StaffForgotPassword() {
  const [result, setResult] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    try { await authRequest("/request-password-reset", { email: values.email, redirectTo: `${window.location.origin}/reset-password/`, turnstileToken }); setResult("If the account exists, a reset email has been sent.") }
    catch (cause) { setResult(cause instanceof ApiError ? cause.message : "If the account exists, a reset email has been sent.") }
  }
  return <AuthForm title="Reset staff password" onSubmit={submit}><Field label="Work email" name="email" type="email" autoComplete="email" /><TurnstileField onToken={setTurnstileToken} /><Button className="mt-6 w-full">Send reset email</Button>{result && <p className="text-muted-foreground mt-4 text-sm">{result}</p>}</AuthForm>
}

export function StaffResetPassword() {
  const token = new URLSearchParams(window.location.search).get("token") ?? ""
  const [result, setResult] = useState("")
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    try { await api("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, newPassword: values.password }) }); setResult("Password updated. You can sign in now.") }
    catch (cause) { setResult(cause instanceof ApiError ? cause.message : "Password could not be updated.") }
  }
  return <AuthForm title="Choose a new password" onSubmit={submit}><PasswordField label="New password" name="password" autoComplete="new-password" /><Button className="mt-6 w-full">Update password</Button>{result && <p className="text-muted-foreground mt-4 text-sm">{result}</p>}</AuthForm>
}

export function StaffInvite() {
  const token = new URLSearchParams(window.location.search).get("token") ?? ""
  const [error, setError] = useState("")
  const [done, setDone] = useState(false)
  const [preview, setPreview] = useState<{ email: string; role: string } | null>(null)
  const [checking, setChecking] = useState(Boolean(token))
  const [turnstileToken, setTurnstileToken] = useState("")
  useEffect(() => {
    let active = true
    if (!token) return () => { active = false }
    void api<{ email: string; role: string }>("/admin-invitations/preview", { method: "POST", body: JSON.stringify({ token }) })
      .then((value) => { if (active) setPreview(value) })
      .catch((cause) => { if (active) setError(cause instanceof ApiError ? cause.message : "Invitation link is invalid or expired.") })
      .finally(() => { if (active) setChecking(false) })
    return () => { active = false }
  }, [token])
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    if (values.password !== values.passwordConfirmation) { setError("Passwords do not match."); return }
    try { await api("/admin-invitations/accept", { method: "POST", body: JSON.stringify({ token, firstName: values.firstName, lastName: values.lastName, password: values.password, turnstileToken }) }); setDone(true) }
    catch (cause) { setError(cause instanceof ApiError ? cause.message : "Invitation could not be accepted.") }
  }
  return <AuthForm title="Create your staff account" onSubmit={submit}>{error && <Alert>{error}</Alert>}{checking && <p className="text-muted-foreground mt-4 text-sm">Checking invitation…</p>}{preview && !done && <p className="bg-muted text-muted-foreground mt-4 rounded-lg p-3 text-sm">{preview.email} · {preview.role}</p>}{done ? <p className="text-muted-foreground mt-4 text-sm">Account created. Return to sign in.</p> : preview && <><Field label="First name" name="firstName" type="text" autoComplete="given-name" /><Field label="Last name" name="lastName" type="text" autoComplete="family-name" /><PasswordField label="Password" name="password" autoComplete="new-password" /><PasswordField label="Confirm password" name="passwordConfirmation" autoComplete="new-password" /><TurnstileField onToken={setTurnstileToken} /><Button className="mt-6 w-full" disabled={checking}>Accept invitation</Button></>}</AuthForm>
}
